import React, { useCallback, useEffect, useState } from "react";
import { api } from "@/lib/api";
import PageHeader from "@/components/app/PageHeader";
import { ShieldCheck } from "lucide-react";

const ACTION_STYLE = {
  create: "bg-emerald-50 text-emerald-800 border-emerald-200",
  update: "bg-blue-50 text-blue-800 border-blue-200",
  delete: "bg-red-50 text-red-800 border-red-200",
  approve: "bg-orange-50 text-orange-800 border-orange-200",
  login: "bg-gray-100 text-gray-800 border-gray-300",
  logout: "bg-gray-100 text-gray-800 border-gray-300",
  export: "bg-purple-50 text-purple-800 border-purple-200",
};

export default function AuditLog() {
  const [entries, setEntries] = useState([]);
  const [filter, setFilter] = useState("");

  const refresh = useCallback(async () => setEntries((await api.get("/audit-log?limit=500")).data), []);
  useEffect(() => { refresh(); }, [refresh]);

  const filtered = filter
    ? entries.filter((e) => (e.entity_type || "").includes(filter) || (e.action || "").includes(filter))
    : entries;

  return (
    <div>
      <PageHeader
        title="Audit Log"
        subtitle="Immutable activity trail"
        actions={
          <div className="flex items-center gap-2 text-xs font-mono text-gray-600">
            <ShieldCheck className="w-4 h-4 text-emerald-600" />
            <span>Append-only · {entries.length} entries</span>
          </div>
        }
      />
      <div className="p-8">
        <div className="mb-4 flex items-center gap-2">
          <input
            type="text"
            value={filter}
            onChange={(e) => setFilter(e.target.value)}
            placeholder="Filter by entity or action (e.g. load, hauler, delete)"
            data-testid="audit-filter-input"
            className="w-full max-w-md px-3 py-2 border border-gray-300 text-sm focus:outline-none focus:border-gray-900 font-mono"
          />
        </div>

        <div className="bg-white border border-gray-200 overflow-x-auto" data-testid="audit-log-table">
          <table className="w-full">
            <thead>
              <tr className="border-b border-gray-200 bg-gray-50">
                <th className="text-left px-4 py-2.5 label-caps">Timestamp</th>
                <th className="text-left px-4 py-2.5 label-caps">Actor</th>
                <th className="text-left px-4 py-2.5 label-caps">Action</th>
                <th className="text-left px-4 py-2.5 label-caps">Entity</th>
                <th className="text-left px-4 py-2.5 label-caps">Entity ID</th>
                <th className="text-left px-4 py-2.5 label-caps">IP</th>
              </tr>
            </thead>
            <tbody>
              {filtered.length === 0 ? (
                <tr><td colSpan="6" className="px-4 py-12 text-center text-sm text-gray-500">No audit entries.</td></tr>
              ) : filtered.map((e) => (
                <tr key={e.id} className="border-b border-gray-100 hover:bg-gray-50" data-testid={`audit-row-${e.id}`}>
                  <td className="px-4 py-2 font-mono text-xs text-gray-700">{e.at?.slice(0, 19).replace("T", " ")}</td>
                  <td className="px-4 py-2 text-sm text-gray-800">{e.user_email}</td>
                  <td className="px-4 py-2">
                    <span className={`text-[10px] uppercase tracking-widest font-bold px-1.5 py-0.5 border ${ACTION_STYLE[e.action] || "bg-gray-100 text-gray-800 border-gray-300"}`}>
                      {e.action}
                    </span>
                  </td>
                  <td className="px-4 py-2 font-mono text-xs text-gray-800">{e.entity_type}</td>
                  <td className="px-4 py-2 font-mono text-[11px] text-gray-500">{e.entity_id?.slice(0, 12)}…</td>
                  <td className="px-4 py-2 font-mono text-[11px] text-gray-500">{e.ip || "—"}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}
